import axios from 'axios';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { omit, isFunction } from 'lodash';
import { mergeDefaultConfig, mergeHandleConfig } from './configs';
import { isNil, guids, isObject } from './utils';
import CryptoJS from 'crypto-js';
import Cookies from 'js-cookie';

const LODALSTORAGE_KEY = 'thales-track-storage';
const TRACK_DATA_KEY = 'thales-track-data';
const LAST_PAGE_KEY = 'thales-html-track-last-page';

export class Track {
  constructor(opts = {}) {
    this._startTime = Date.now();
    this.timeMap = new Map();
    this._opts = opts;
    this.active = isNil(opts.active) || opts.active;
    this.config = mergeDefaultConfig(opts);
    this.sourceChannel = opts.sourceChannel;
    this.useChannel = opts.useChannel;
    this._deviceId = '';
    this.tempParams = {};
    // 上一个页面地址（跨页面通过 sessionStorage 记忆）
    this._fromPage =
      (typeof sessionStorage !== 'undefined' &&
        sessionStorage.getItem(LAST_PAGE_KEY)) ||
      '';
    this._currentRoute = this._getCurrentUrl();
    this._historyOriginals = {};
    this._isBeforeUnloadReporting = false;
    this.getDeviceId();
    this._setupRouteListeners();
  }

  _setupRouteListeners() {
    this._boundRouteHandler = this._handleRouteChange.bind(this);
    this._boundBeforeUnload = this._handleBeforeUnload.bind(this);
    window.addEventListener('hashchange', this._boundRouteHandler, true);
    window.addEventListener('popstate', this._boundRouteHandler, true);
    window.addEventListener('beforeunload', this._boundBeforeUnload, true);
    this._patchHistoryMethod('pushState');
    this._patchHistoryMethod('replaceState');
  }

  _patchHistoryMethod(method) {
    const original = window.history[method];
    if (typeof original !== 'function' || this._historyOriginals[method])
      return;
    this._historyOriginals[method] = original;
    const track = this;
    window.history[method] = function patchedHistoryMethod(...args) {
      const result = track._historyOriginals[method].apply(this, args);
      track._handleRouteChange();
      return result;
    };
  }

  _handleRouteChange() {
    if (!this.active || this._isBeforeUnloadReporting) {
      this._isBeforeUnloadReporting = false;
      return;
    }
    const nextRoute = this._getCurrentUrl();
    if (nextRoute === this._currentRoute) {
      return;
    }
    this._reportCurrentRoute();
    this._currentRoute = nextRoute;
  }

  _handleBeforeUnload() {
    this._isBeforeUnloadReporting = true;
    // 不使用 sendBeacon，直接走与路由切换相同的上报逻辑
    this._reportCurrentRoute();
  }

  _reportCurrentRoute() {
    if (!this._currentRoute || this._shouldSkipRoute(this._currentRoute)) {
      this.resetTime();
      this._fromPage = this._currentRoute;
      return;
    }
    const title = this._getPageTitle();
    this.reportView(this._currentRoute, title);
  }

  _getPageTitle() {
    try {
      return typeof document !== 'undefined' && document.title
        ? document.title
        : '';
    } catch (e) {
      return '';
    }
  }

  _shouldSkipRoute(route) {
    const { excludes } = this._opts;
    if (!this.active) return true;
    if (Array.isArray(excludes)) {
      return excludes.some((part) => {
        if (typeof part === 'string') {
          return route && route.startsWith(part);
        } else if (part instanceof RegExp) {
          return part.test(route);
        }
        return false;
      });
    }
    if (isFunction(excludes)) {
      return excludes(route);
    }
    return false;
  }

  _getCurrentUrl() {
    const { pathname, search, hash } = window.location || {};
    return `${pathname || ''}${search || ''}${hash || ''}` || '/';
  }

  resetTime() {
    this._startTime = Date.now();
  }

  async getDeviceId() {
    const fpPromise = FingerprintJS.load();
    const fp = await fpPromise;
    const result = await fp.get();
    this._deviceId = result.visitorId;
  }

  throttle(func, delay = 1000, omits = [], opts) {
    const base64 = this.objToBase64(omit(opts, omits));
    if (!this.timeMap.get(base64)) {
      this.timeMap.set(
        base64,
        setTimeout(() => {
          this.timeMap.delete(base64);
        }, delay)
      );
      return func.apply(this, [opts]);
    }
  }

  getHash(url) {
    let sliceStart = url.indexOf('#');
    let sliceEnd = url.indexOf('?') || undefined;
    if (sliceStart > -1) {
      return url.slice(sliceStart + 1, sliceEnd > -1 ? sliceEnd : undefined);
    } else {
      return url || '/';
    }
  }

  getToken() {
    return (
      sessionStorage.getItem('TokenKey') ||
      localStorage.getItem('TokenKey') ||
      Cookies.get('TokenKey') ||
      sessionStorage.getItem('token') ||
      localStorage.getItem('token') ||
      Cookies.get('token') ||
      sessionStorage.getItem('Token') ||
      localStorage.getItem('Token') ||
      Cookies.get('Token') ||
      (this.getStorage('token') &&
        this.getStorage('token').replace(/^Bearer /, '')) ||
      '-1'
    );
  }

  getProductName() {
    return (
      sessionStorage.getItem('TrackProductName') ||
      localStorage.getItem('TrackProductName') ||
      Cookies.get('TrackProductName') ||
      ''
    );
  }

  report(opts = {}) {
    try {
      const options = mergeHandleConfig(opts);
      const {
        debug = false,
        env,
        protocol = 'https',
        data = null,
        dataHandler = null,
        requestHandler = null,
        paramsHandler = null,
      } = this.config;
      const opt = {};
      if (typeof data === 'object' && data !== null) {
        Object.assign(opt, data);
      } else if (typeof data === 'function') {
        Object.assign(opt, data({}));
      }
      if (typeof dataHandler === 'function') {
        Object.assign(opt, dataHandler({}));
      }
      if (
        !this.tempParams.distinct_id &&
        Object.prototype.hasOwnProperty.call(opt, 'userId') &&
        !opt.userId
      )
        return;
      if (
        !this.tempParams.distinct_id &&
        Object.prototype.hasOwnProperty.call(opt, 'userid') &&
        !opt.userid
      )
        return;
      if (
        !this.tempParams.distinct_id &&
        Object.prototype.hasOwnProperty.call(opt, 'user_id') &&
        !opt.user_id
      )
        return;
      const params = this.getDefaultOpts(options);
      if (debug && env !== 'prod') {
        console.log('debug', params);
      }
      if (params.duration_time > 2592000) {
        throw new Error(
          `页面停留时间异常\n${
            navigator && navigator.userAgent
          }\n${JSON.stringify(params)}`
        );
      }
      if (requestHandler && typeof requestHandler === 'function') {
        return requestHandler(params);
      }
      const payload =
        typeof paramsHandler === 'function' ? paramsHandler(params) : params;
      const dataString = this.objToBase64(payload);
      if (typeof fetch === 'function') {
        return fetch(
          `${protocol}://user-web.yunlizhi.cn/bfs_toolkit/events/report?data=${dataString}`,
          {
            method: 'GET',
            // keepalive 允许在页面卸载时继续发送请求
            keepalive: true,
          }
        );
      }
      return axios({
        method: 'get',
        url: `${protocol}://user-web.yunlizhi.cn/bfs_toolkit/events/report?data=${dataString}`,
      });
    } catch (error) {
      console.log('埋点插件报错', error);
    }
  }

  reportView(oldURL, name_zh = '', _fromPage) {
    try {
      this.throttle(this.report, 1000, ['duration_time'], {
        name_zh,
        trackName: this.getHash(oldURL),
        from:
          _fromPage || (this._fromPage && this.getHash(this._fromPage)) || '',
        trackType: 'view',
        duration_time: (Date.now() - this._startTime) / 1000,
      });
    } finally {
      this._startTime = Date.now();
      this._fromPage = oldURL;
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(LAST_PAGE_KEY, oldURL);
      }
    }
  }

  getStorage(key) {
    let userInfo =
      sessionStorage.getItem('userInfo') ||
      localStorage.getItem('userInfo') ||
      Cookies.get('userInfo') ||
      false;
    userInfo = userInfo ? JSON.parse(userInfo) : false;
    if (userInfo && userInfo[key]) {
      return JSON.stringify(userInfo[key]);
    } else {
      let user =
        sessionStorage.getItem('user') ||
        localStorage.getItem('user') ||
        Cookies.get('user') ||
        false;
      if (user === false) {
        return '';
      }
      user = JSON.parse(user);
      if (user && user.userInfo && user.userInfo[key]) {
        return JSON.stringify(user.userInfo[key]);
      } else {
        return '';
      }
    }
  }

  getWorkbenchCode() {
    try {
      const currentWorkbench = JSON.parse(
        sessionStorage.getItem('launchboard:currentWorkbench') ||
          localStorage.getItem('launchboard:currentWorkbench') ||
          Cookies.get('launchboard:currentWorkbench') ||
          sessionStorage.getItem('currentWorkbench') ||
          localStorage.getItem('currentWorkbench') ||
          Cookies.get('currentWorkbench') ||
          null
      );
      return currentWorkbench && currentWorkbench.workbenchCode;
    } catch (error) {
      console.log('获取工作台信息报错', error);
      return null;
    }
  }

  strFilter(val = '') {
    return val.replace(/"/g, '');
  }

  getDefaultOpts(opts) {
    const {
      domainId: domain_id,
      appName: app,
      env,
      debug = false,
      data = null,
      dataHandler = null,
      systemCode,
      code,
    } = this.config;
    const optsNew = JSON.parse(JSON.stringify(opts));
    let event = optsNew.event || optsNew.trackType;
    optsNew.name = opts.trackName;
    optsNew.name_zh =
      optsNew.name_zh || optsNew.trackNameZh || optsNew.trackName || '';
    optsNew.user_agent = window.navigator.userAgent;
    optsNew.p__device_id = this._deviceId;
    optsNew.source_channel = this.sourceChannel;
    optsNew.use_channel = this.useChannel;
    optsNew.workstand_id = this.getWorkbenchCode();
    const trackData =
      localStorage.getItem(TRACK_DATA_KEY) ||
      sessionStorage.getItem(TRACK_DATA_KEY);
    if (trackData) {
      const trackDataObj = JSON.parse(trackData);
      Object.assign(optsNew, trackDataObj);
    }
    if (typeof data === 'object' && data !== null) {
      Object.assign(optsNew, data);
    } else if (typeof data === 'function') {
      Object.assign(optsNew, data(optsNew));
    }
    if (typeof dataHandler === 'function') {
      Object.assign(optsNew, dataHandler(optsNew));
    }
    delete optsNew.trackType;
    delete optsNew.trackName;
    const distinct_id = this.strFilter(
      optsNew.userId ||
        optsNew.userid ||
        optsNew.user_id ||
        sessionStorage.getItem('userId') ||
        localStorage.getItem('userId') ||
        Cookies.get('userId') ||
        this.getStorage('userId') ||
        ''
    );
    if (!this.tempParams) {
      this.tempParams = {};
    }
    const params = {
      debug: debug && env === 'dev',
      app,
      system_code: this.tempParams.system_code || systemCode,
      code,
      domain_id,
      project: `ylz_${env}`,
      session_id: this.strFilter(this.getToken()),
      uuid: guids(),
      distinct_id:
        this.tempParams.distinct_id || distinct_id || this.getDistinctId(),
      time: Date.now(),
      type: 'track',
      event,
      product_name: this.getProductName() || '',
      properties: {
        ...optsNew,
        tenantname: this.tempParams.tenantname || optsNew.tenantname,
        tenantid: this.tempParams.tenantid || optsNew.tenantid,
      },
    };
    this.tempParams = {
      tenantname: optsNew.tenantname,
      tenantid: optsNew.tenantid,
      distinct_id,
      system_code: systemCode,
    };
    return {
      ...params,
    };
  }

  getDistinctId() {
    const guidLocal =
      sessionStorage.getItem('trackGuid') ||
      localStorage.getItem('trackGuid') ||
      Cookies.get('trackGuid');
    if (isNil(guidLocal)) {
      const guid = guids();
      localStorage.setItem('trackGuid', guid);
      return guid;
    } else {
      return guidLocal;
    }
  }

  objToBase64(data) {
    const wordArray = CryptoJS.enc.Utf8.parse(JSON.stringify(data));
    const base64 = CryptoJS.enc.Base64url.stringify(wordArray, false);
    return base64;
  }
}

const HtmlTrack = {
  init(opt = {}) {
    if (isNil(opt) || !isObject(opt)) {
      throw new Error(`The config must be object, but got ${opt}`);
    }
    const track = new Track(opt);
    window.YlTrack = track;
    return track;
  },
};

if (typeof window !== 'undefined') {
  // 覆盖 UMD 预先挂到 window.HtmlTrack 的导出对象，直接暴露带 init 的插件对象
  window.HtmlTrack = HtmlTrack;
}

export default HtmlTrack;
